/**
 * Non-React WebSocket session connection for the call screen.
 *
 * This class owns the live-session WebSocket, the auth handshake sequencing,
 * and the reconnect state machine. It exists to lift those concerns out of the
 * ~380-line startSession useCallback in useSessionWebSocket, where they lived as
 * closure variables invisible to React and to tests (the A8 reconnect bugs lived
 * there). Here every piece of that machine is an inspectable field: `state`,
 * `reconnectAttempts`, `cancelled`, `isConnected`.
 *
 * The class is intentionally free of React and of message-routing logic: it
 * parses each server frame and forwards it via `onServerMessage`, leaving the
 * React-coupled handlers (status text, video recording, audio gate) to the
 * useSessionWebSocket adapter.
 */

import { FATAL_CLOSE_CODES } from './protocol-types';

/**
 * Explicit connection state, modeled from the status strings the hook used to
 * compare against. Status-string comparisons across the call screen should key
 * off this enum instead of magic strings.
 */
export enum ConnectionState {
  /** No socket opened yet. */
  Idle = 'idle',
  /** Socket opening, auth not yet sent. */
  Connecting = 'connecting',
  /** Auth message sent, waiting for the worker to confirm. */
  Authenticating = 'authenticating',
  /** Worker confirmed (connected or reconnected); the call is live. */
  Connected = 'connected',
  /** Lost the socket after being live; cycling through backoff reconnects. */
  Reconnecting = 'reconnecting',
  /** Intentionally torn down (cancel/close); no reconnect will run. */
  Closed = 'closed',
  /** Unrecoverable close (bad credentials, no credits, duplicate, exhausted). */
  Fatal = 'fatal',
}

/** Auth credentials for the first WebSocket message (SEC-5: never in the URL). */
export interface SessionConnectionAuth {
  /** Same-session scoped grant required for the multimodal response. */
  responseToken?: string;
  /** Explicit participation intent, never inferred from device state. */
  participationMode?: 'text' | 'call';
  /** Preferred: passcode-free auth keyed on the interview_data_id. */
  interviewDataId?: string;
  /** Fallback when no interview_data_id is available. */
  passcode?: string;
  /** Optional language override forwarded to the worker. */
  enforceLanguage?: string | null;
}

/** Details of a close the class classified as fatal (no reconnect possible). */
export interface FatalClose {
  /**
   * WebSocket close code (one of FATAL_CLOSE_CODES), or null
   * when reconnect attempts ran out.
   */
  code: number | null;
  /** Server-supplied reason, when present. */
  reason?: string;
}

/** A close the class did not handle itself (the adapter decides what to do). */
export interface UnhandledClose {
  /** WebSocket close code. */
  code: number;
  /** Server-supplied reason, when present. */
  reason?: string;
  /** True if the call had reached Connected before this close. */
  wasConnected: boolean;
}

/** Typed event callbacks the adapter subscribes to. */
export interface SessionConnectionEvents {
  /** Fired whenever `state` transitions. */
  onStateChange: (state: ConnectionState) => void;
  /** Fired with each parsed server frame for the adapter to route. */
  onServerMessage: (message: unknown) => void;
  /** Fired on a transport-level error event (generic; close carries detail). */
  onError: (error: Event) => void;
  /** Fired once when the connection reaches a terminal, unrecoverable close. */
  onFatal: (close: FatalClose) => void;
  /**
   * Fired for a close the class did not handle as fatal or reconnect (e.g. a
   * pre-Connected 1006 the adapter disambiguates, or a clean 1000). Optional.
   */
  onClose?: (close: UnhandledClose) => void;
}

/** Construction options. */
export interface SessionConnectionOptions {
  /** Full ws(s):// URL including room_id (auth params go in the auth message). */
  wsUrl: string;
  /** Auth credentials for the handshake. */
  auth: SessionConnectionAuth;
  /** Reconnect cap before declaring a fatal close. */
  maxReconnectAttempts: number;
  /** Typed event callbacks. */
  events: SessionConnectionEvents;
}

/**
 * Owns the WebSocket, auth handshake, and reconnect machine for one call.
 */
export class SessionConnection {
  /** Current connection state. */
  public state: ConnectionState = ConnectionState.Idle;
  /** Current reconnect attempt (0 when connected or idle). */
  public reconnectAttempts = 0;
  /**
   * True once teardown (cancel/close) has run. A reconnect whose backoff timer
   * is still pending checks this after the wait so an unmount mid-backoff cannot
   * spawn a zombie socket that re-occupies the room and blocks the next join.
   */
  public cancelled = false;

  private ws: WebSocket | null = null;
  private readonly wsUrl: string;
  private readonly auth: SessionConnectionAuth;
  private readonly maxReconnectAttempts: number;
  private readonly events: SessionConnectionEvents;
  /**
   * Mirrors the hook's old connectedRef. Cleared by an onerror/onclose and
   * restored on the connected/reconnected handshake so downstream mic
   * auto-recovery stays armed across reconnects.
   */
  private connected = false;
  /** True once the call has reached Connected at least once this lifecycle. */
  private everConnected = false;
  /**
   * Armed by a `disconnected` server frame: the worker gave up mid-interview,
   * fully cleaned up the durable object, and is about to close the socket (code
   * 1000 'Cleanup complete'). The very next close triggers a single silent
   * rejoin against the fresh durable object, which resumes the interview from
   * the turns persisted in the database. The kicked path never sends this frame
   * (it closes cleanly), and user-ended / interview_ended tear the connection
   * down (detaching handlers) before the frame lands, so those closes stay
   * terminal.
   */
  private disconnectRecoveryPending = false;
  /** An authoritative media ending stays terminal while the UI flushes recordings. */
  private serverEnded = false;
  /**
   * True only while the single disconnect-recovery socket is in flight. Its
   * failure goes straight to fatal instead of cascading into the 1006 backoff
   * loop: this recovery path makes exactly one attempt.
   */
  private inDisconnectRecovery = false;

  /**
   * @param options - Connection URL, auth, reconnect cap, and event callbacks.
   */
  constructor(options: SessionConnectionOptions) {
    this.wsUrl = options.wsUrl;
    this.auth = options.auth;
    this.maxReconnectAttempts = options.maxReconnectAttempts;
    this.events = options.events;
  }

  /**
   * True while the call is live (set on connected/reconnected, cleared on
   * error/close). Downstream mic auto-recovery gates on this.
   */
  public get isConnected(): boolean {
    return this.connected;
  }

  /** The live WebSocket, or null when none is open. */
  public get socket(): WebSocket | null {
    return this.ws;
  }

  /**
   * Open the initial socket and start the handshake. Re-arms reconnects if a
   * prior teardown left the cancel flag set.
   */
  public connect(): void {
    this.cancelled = false;
    this.serverEnded = false;
    this.reconnectAttempts = 0;
    this.everConnected = false;
    this.disconnectRecoveryPending = false;
    this.inDisconnectRecovery = false;
    this.createAndWireSocket();
  }

  /** Send a frame on the live socket if it is open. No-op otherwise. */
  public send(data: unknown): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(typeof data === 'string' ? data : JSON.stringify(data));
    }
  }

  /**
   * Intentionally tear the connection down. Cancels any pending reconnect,
   * detaches handlers, and closes the socket. No further events fire.
   */
  public close(): void {
    this.cancelled = true;
    this.connected = false;
    const ws = this.ws;
    this.ws = null;
    if (ws) {
      this.detachHandlers(ws);
      try { ws.close(); } catch { /* ignore */ }
    }
    this.setState(ConnectionState.Closed);
  }

  /**
   * Cancel any pending reconnect without necessarily closing an open socket.
   * Used by teardown paths that manage the socket separately.
   */
  public cancel(): void {
    this.cancelled = true;
    this.connected = false;
    if (this.ws) {
      this.detachHandlers(this.ws);
      this.ws = null;
    }
    this.setState(ConnectionState.Closed);
  }

  /** Transition state and notify subscribers (only on an actual change). */
  private setState(next: ConnectionState): void {
    if (this.state === next) return;
    this.state = next;
    this.events.onStateChange(next);
  }

  /** Remove our handlers from a socket so a late event cannot reach us. */
  private detachHandlers(ws: WebSocket): void {
    ws.onopen = null;
    ws.onmessage = null;
    ws.onerror = null;
    ws.onclose = null;
  }

  /**
   * Create a new WebSocket and wire its handlers. Used for both the initial
   * connection and every reconnect attempt.
   */
  private createAndWireSocket(): void {
    const ws = new WebSocket(this.wsUrl);
    this.ws = ws;
    this.setState(
      this.reconnectAttempts > 0 ? ConnectionState.Reconnecting : ConnectionState.Connecting,
    );

    ws.onopen = () => {
      if (this.ws !== ws) return;
      // SEC-5: auth is the first WS message (kept out of the URL).
      if (this.auth.interviewDataId) {
        ws.send(JSON.stringify({
          type: 'auth',
          interview_data_id: this.auth.interviewDataId,
          enforce_language: this.auth.enforceLanguage ?? null,
          ...(this.auth.responseToken ? { response_token: this.auth.responseToken, participation_mode: this.auth.participationMode ?? 'call' } : {}),
        }));
      } else {
        ws.send(JSON.stringify({
          type: 'auth',
          passcode: this.auth.passcode,
          enforce_language: this.auth.enforceLanguage ?? null,
        }));
      }
      // Stay in Reconnecting during a reconnect handshake; otherwise authenticate.
      if (this.state !== ConnectionState.Reconnecting) {
        this.setState(ConnectionState.Authenticating);
      }
    };

    ws.onmessage = (event: MessageEvent) => {
      if (this.ws !== ws) return;
      let message: unknown;
      try {
        message = JSON.parse(event.data);
      } catch {
        return;
      }

      const type = (message as { type?: string })?.type;
      if (type === 'connected' || type === 'reconnected') {
        // Handshake complete: the call is live and the connected gate is open
        // again (restoring mic auto-recovery after a reconnect cleared it). A
        // completed handshake also means any in-flight disconnect-recovery
        // socket stuck, so clear its one-shot guard: a later worker disconnect
        // may arm its own single rejoin.
        this.connected = true;
        this.everConnected = true;
        this.reconnectAttempts = 0;
        this.inDisconnectRecovery = false;
        this.setState(ConnectionState.Connected);
      } else if (type === 'call_ended' || type === 'interview_ended') {
        this.serverEnded = true;
        this.disconnectRecoveryPending = false;
      } else if (type === 'disconnected' && !this.serverEnded) {
        // The worker is tearing down the durable object mid-interview. Arm a
        // single silent rejoin for the 1000 close that immediately follows.
        this.disconnectRecoveryPending = true;
      }

      this.events.onServerMessage(message);
    };

    ws.onerror = (error: Event) => {
      if (this.ws !== ws) return;
      // Transport errors are generic and often precede close; defer
      // classification to onclose. Clear the connected gate for now.
      this.connected = false;
      this.events.onError(error);
    };

    ws.onclose = (event: CloseEvent) => {
      if (this.ws !== ws) return;
      this.connected = false;
      this.handleClose(event);
    };
  }

  /**
   * Classify a close. Fatal codes terminate the connection; a 1006 after being
   * live drives the reconnect machine; anything else closes cleanly.
   */
  private handleClose(event: CloseEvent): void {
    if (this.cancelled || this.serverEnded) {
      this.setState(ConnectionState.Closed);
      return;
    }

    // Worker-defined fatal codes (shared source of truth in the worker types):
    // invalid credentials (4001), private conversation unavailable (4002),
    // retryable service unavailable (4003), or non-retryable realtime
    // unavailable (4004). 1008 is intentionally absent.
    if ((FATAL_CLOSE_CODES as readonly number[]).includes(event.code)) {
      this.setState(ConnectionState.Fatal);
      this.events.onFatal({ code: event.code, reason: event.reason });
      return;
    }

    // The single disconnect-recovery socket dropped before completing its
    // handshake. This path is one-shot by contract, so do NOT fall into the
    // 1006 backoff loop: go fatal so the adapter surfaces the existing
    // Connection Error screen (the user can still retry manually).
    if (this.inDisconnectRecovery) {
      this.inDisconnectRecovery = false;
      this.setState(ConnectionState.Fatal);
      this.events.onFatal({ code: null, reason: 'disconnect recovery failed' });
      return;
    }

    // Worker-initiated mid-interview disconnect: a `disconnected` frame armed
    // this, and the durable object then closed (code 1000 'Cleanup complete').
    // The object has fully torn down, so a fresh socket resumes the interview
    // from the persisted turns. Attempt exactly one silent rejoin.
    if (this.disconnectRecoveryPending && this.everConnected) {
      this.disconnectRecoveryPending = false;
      void this.attemptDisconnectRecovery();
      return;
    }

    if (event.code === 1006) {
      // Mid-cycle 1006: advance to the next attempt.
      if (this.reconnectAttempts > 0) {
        void this.attemptReconnect(this.reconnectAttempts + 1);
        return;
      }
      // Post-Connected 1006: start the reconnect machine.
      if (this.everConnected) {
        void this.attemptReconnect(1);
        return;
      }
      // Pre-Connected 1006: never reached the call. The adapter disambiguates
      // (occupancy conflict vs generic network failure) rather than reconnect.
      this.setState(ConnectionState.Closed);
      this.events.onClose?.({ code: event.code, reason: event.reason, wasConnected: false });
      return;
    }

    // Any other code is a clean, intentional-enough close.
    this.setState(ConnectionState.Closed);
    this.events.onClose?.({ code: event.code, reason: event.reason, wasConnected: this.everConnected });
  }

  /**
   * Reconnect with exponential backoff. After every await hop it re-checks the
   * cancel flag so an unmount mid-backoff cannot spawn a post-teardown socket.
   *
   * @param attempt - 1-based attempt number.
   */
  private async attemptReconnect(attempt: number): Promise<void> {
    // Cancelled wins over the fatal path: a torn-down session reconnects to
    // nothing, silently.
    if (this.cancelled) {
      this.setState(ConnectionState.Closed);
      return;
    }

    if (attempt > this.maxReconnectAttempts) {
      this.setState(ConnectionState.Fatal);
      this.events.onFatal({ code: null, reason: 'reconnect attempts exhausted' });
      return;
    }

    this.reconnectAttempts = attempt;
    this.setState(ConnectionState.Reconnecting);

    const delay = Math.min(2000 * Math.pow(2, attempt - 1), 8000); // 2s, 4s, 8s
    await new Promise((resolve) => setTimeout(resolve, delay));

    // Re-check after the backoff hop: teardown may have landed while we waited.
    if (this.cancelled) {
      this.setState(ConnectionState.Closed);
      return;
    }

    try {
      this.createAndWireSocket();
    } catch {
      void this.attemptReconnect(attempt + 1);
    }
  }

  /**
   * Recover from a worker-initiated mid-interview disconnect with a single
   * silent rejoin. Unlike attemptReconnect this makes exactly one attempt: the
   * fresh socket either completes its handshake (resuming the interview from
   * the persisted turns) or, on failure, drives the connection fatal via the
   * inDisconnectRecovery guard in handleClose. A short backoff runs first so
   * the durable object finishes tearing down before the fresh socket lands.
   */
  private async attemptDisconnectRecovery(): Promise<void> {
    if (this.cancelled) {
      this.setState(ConnectionState.Closed);
      return;
    }

    this.inDisconnectRecovery = true;
    // Surface the existing Reconnecting state (attempt 1 of the cap) so the orb
    // shows its connecting animation while the rejoin is in flight.
    this.reconnectAttempts = 1;
    this.setState(ConnectionState.Reconnecting);

    // Match the first 1006 backoff hop so the durable object settles first.
    await new Promise((resolve) => setTimeout(resolve, 2000));

    // Re-check after the backoff hop: teardown may have landed while we waited.
    if (this.cancelled) {
      this.setState(ConnectionState.Closed);
      return;
    }

    try {
      this.createAndWireSocket();
    } catch {
      this.inDisconnectRecovery = false;
      this.setState(ConnectionState.Fatal);
      this.events.onFatal({ code: null, reason: 'disconnect recovery failed' });
    }
  }
}
